<template>
  <div
    v-if="userDataLoaded && currentInternalNotification"
    class="sticky-notification-bar"
  >
    <div class="notification-content">
      <h4 class="notification-title">{{ currentInternalNotification.localizedContent && currentInternalNotification.localizedContent.length > 0 ? currentInternalNotification.localizedContent[0].title : '' }}</h4>
      <p
        class="notification-text"
        v-html="currentInternalNotification.localizedContent && currentInternalNotification.localizedContent.length > 0 ? currentInternalNotification.localizedContent[0].content : ''"
      ></p>
      <BButton
        ref="closeButton"
        class="btn close-button"
        @click="closeNotification"
      >
        <b-icon
          icon="plus-circle"
          class="close-icon"
        />
        {{ translate('closeButton', labels, 'shared_CommonFunctional') }}
      </BButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import {ref, computed, watch} from 'vue'
import {useStore} from 'vuex'
import {notificationsService} from '@/services/notifications.service.js'
import config from '@/config'
import {useCommon} from '@/composables/useCommon'

const {labels, translate} = useCommon()
const store = useStore()

const userDataLoaded = ref(false)
const defaultLanguage = 'en'
const currentInternalNotification = ref<any>(null)
const seenNotificationIds = ref<string[]>([])
const rawAnnouncements = ref<any[]>([])
const announcements = ref<any[]>([])
const loading = ref(false)
const error = ref<any>(null)

const getLanguage = computed(() => store.getters['mymachine/getLanguage'])
const getRegion = computed(() => store.getters['mymachine/getRegion'])
const identity = computed(() => store.getters['mymachine/getIdentity'])

const localStorageKey = computed(() => `seenNotifications_${identity.value.identityId}`)

const unreadNotifications = computed(() => {
  if (!announcements.value || announcements.value.length === 0) {
    return []
  }
  return announcements.value.filter(
    (announcement: any) => !seenNotificationIds.value.includes(announcement.id)
  )
})

watch(unreadNotifications, (newUnread) => {
  if (newUnread.length > 0) {
    currentInternalNotification.value = newUnread[0]
  } else {
    currentInternalNotification.value = null
  }
}, {immediate: true, deep: true})

watch(getLanguage, () => {
  prepareAnnouncements()
})

watch(identity, () => {
  loadSeenNotifications()
})

watch([getRegion, identity], ([region, user]) => {
  const hasRegion = !!region
  const hasIdentity = !!(user && user.identityId)
  if (hasRegion && hasIdentity) {
    fetchAnnouncements()
  }
}, {immediate: true})

function prepareAnnouncements(): void {
  announcements.value = rawAnnouncements.value.map((item: any) => ({
    id: item.id,
    localizedContent: item.contents && item.contents[getLanguage.value] ? [
      {
        language: [getLanguage.value],
        title: item.contents[getLanguage.value].title,
        content: item.contents[getLanguage.value].content
      }
    ] : [
      {
        language: [defaultLanguage],
        title: item.contents[defaultLanguage].title,
        content: item.contents[defaultLanguage].content
      }
    ]
  }))
}

async function fetchAnnouncements(): Promise<void> {
  if (!getRegion.value || !identity.value || !identity.value.identityId) {
    rawAnnouncements.value = []
    announcements.value = []
    return
  }

  loading.value = true
  error.value = null

  try {
    const response = await notificationsService.fetchActiveNotifications(String(config.enigmaAppId), String(getRegion.value || ''))
    if (response && response.items) {
      rawAnnouncements.value = response.items
      prepareAnnouncements()
    } else {
      rawAnnouncements.value = []
    }
  } catch (err) {
    error.value = err
    rawAnnouncements.value = []
  }
  loading.value = false
}

function loadSeenNotifications(): void {
  try {
    const storedIds = localStorage.getItem(localStorageKey.value)
    seenNotificationIds.value = storedIds ? JSON.parse(storedIds) : []
  } catch (e) {
    seenNotificationIds.value = []
  } finally {
    userDataLoaded.value = true
  }
}

function saveSeenNotifications(): void {
  try {
    localStorage.setItem(localStorageKey.value, JSON.stringify(seenNotificationIds.value))
  } catch (e) {
    // do nothing
  }
}

function closeNotification(): void {
  if (currentInternalNotification.value) {
    const idToMark = currentInternalNotification.value.id
    if (!seenNotificationIds.value.includes(idToMark)) {
      seenNotificationIds.value.push(idToMark)
      saveSeenNotifications()
    }
  }
}
</script>
